/* oxlint-disable typescript/no-explicit-any */
import { Button } from '@cian/ui-kit/button';
import { CheckboxButton } from '@cian/ui-kit/checkbox';
import { ModalWindow } from '@cian/ui-kit/modal';
import * as React from 'react';

import * as style from './FiltersDesktop.css';
import { trackApplyFiltersClick } from './tracking';
import { useFavoritesUxfeedbackCampaign } from '../../hooks/uxfeedback';
import { EEntityType, EDealType } from '../../types/filters';
import { IResultsState, EGetFavoriteEntitiesStatus } from '../../types/results';

export interface IFiltersDesktopProps {
  filters: {
    dealType: EDealType | null;
    favoriteEntityType: EEntityType | null;
    showFiltersModal?: boolean;
  };
  results: IResultsState;

  setDealType(dealType: EDealType | null): void;
  setFavoriteEntityType(favoriteEntityType: EEntityType | null): void;
  getFavoriteEntities(): void;
  setShowFiltersModal(showFiltersModal: boolean): void;
}

interface IDealTypeButton {
  name: string;
  dealType: EDealType | null;
}

const DEAL_TYPE_BUTTONS: IDealTypeButton[] = [
  { name: 'Неважно', dealType: null },
  { name: 'Аренда', dealType: EDealType.Rent },
  { name: 'Продажа', dealType: EDealType.Sale },
];

interface IFavoriteEntityTypeButton {
  name: string;
  favoriteEntityType: EEntityType | null;
}

const FAVORITE_ENTITY_TYPE_BUTTONS: IFavoriteEntityTypeButton[] = [
  { name: 'Неважно', favoriteEntityType: null },
  { name: 'Жилая', favoriteEntityType: EEntityType.Residential },
  { name: 'Загородная', favoriteEntityType: EEntityType.Suburban },
  { name: 'Коммерческая', favoriteEntityType: EEntityType.Commercial },
];

export const FiltersDesktop: React.FC<IFiltersDesktopProps> = ({
  filters,
  results,
  setDealType,
  setFavoriteEntityType,
  getFavoriteEntities,
  setShowFiltersModal,
}) => {
  const isFiltersDisabled =
    !filters.dealType &&
    !filters.favoriteEntityType &&
    results.status === EGetFavoriteEntitiesStatus.Succeed &&
    results.favoriteEntities.length === 0;

  const [dealTypeState, setDealTypeState] = React.useState<EDealType | null>(filters.dealType);
  const [favoriteEntityTypeState, setFavoriteEntityTypeState] = React.useState<EEntityType | null>(
    filters.favoriteEntityType,
  );

  const { sendFavoritesUxfeedbackCampaign } = useFavoritesUxfeedbackCampaign();

  const handleSetDealTypeClick = (dealType: EDealType | null) => {
    setDealTypeState(dealType);
  };

  const handleSetFavoriteTypeClick = (favoriteType: EEntityType | null) => {
    setFavoriteEntityTypeState(favoriteType);
  };

  const handleAcceptFilterClick = () => {
    setFavoriteEntityType(favoriteEntityTypeState);
    setDealType(dealTypeState);
    getFavoriteEntities();
    setShowFiltersModal(false);
    trackApplyFiltersClick({ dealType: dealTypeState, favoriteEntityType: favoriteEntityTypeState });
    sendFavoritesUxfeedbackCampaign();
  };

  const handleResetFilterClick = () => {
    handleSetDealTypeClick(null);
    handleSetFavoriteTypeClick(null);
    setFavoriteEntityType(null);
    setDealType(null);
    getFavoriteEntities();
    setShowFiltersModal(false);
  };

  const renderModalContent = () => (
    <div className={style['filters-container']} data-name="FiltersDesktop">
      <div className={style['row']}>
        <span className={style['filters-title']}>Тип сделки</span>
        <div className={style['filters-buttons']}>
          {DEAL_TYPE_BUTTONS.map(button => {
            const isActive = dealTypeState === button.dealType;

            return (
              <span key={`dealType_${button.dealType}`} className={style['chipsbox-wrapper']}>
                <CheckboxButton
                  label={button.name}
                  checked={isActive && !isFiltersDisabled}
                  disabled={isFiltersDisabled}
                  onChange={() => handleSetDealTypeClick(!isActive ? button.dealType : null)}
                />
              </span>
            );
          })}
        </div>
      </div>
      <div className={style['row']}>
        <span className={style['filters-title']}>Вид недвижимости</span>
        <div className={style['filters-buttons']}>
          {FAVORITE_ENTITY_TYPE_BUTTONS.map(button => {
            const isActive = favoriteEntityTypeState === button.favoriteEntityType;
            if (
              button.favoriteEntityType === EEntityType.Newbuilding &&
              !!filters.dealType &&
              filters.dealType !== EDealType.Sale
            ) {
              return null as any;
            }

            return (
              <span key={`favoriteEntityType_${button.favoriteEntityType}`} className={style['chipsbox-wrapper']}>
                <CheckboxButton
                  label={button.name}
                  checked={isActive && !isFiltersDisabled}
                  disabled={isFiltersDisabled}
                  onChange={() => handleSetFavoriteTypeClick(!isActive ? button.favoriteEntityType : null)}
                />
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );

  const renderModalFooter = () => (
    <div className={style['footer-modal']}>
      <Button size="XS" theme="fill_white_primary" onClick={handleResetFilterClick}>
        Сбросить фильтры
      </Button>
      <Button size="XS" theme="fill_primary" onClick={handleAcceptFilterClick}>
        Применить
      </Button>
    </div>
  );

  return (
    <ModalWindow
      content={renderModalContent()}
      footer={renderModalFooter()}
      width={702}
      height={325}
      onClose={() => setShowFiltersModal(false)}
      open={Boolean(filters.showFiltersModal)}
      outside
      title="Фильтр"
    />
  );
};
